import { type Styles } from 'react-modal'
import { type Moment } from 'moment'
import { type PartnerPickRecurringShiftsRecurringShift } from 'domains/partner-pick-devices/api'
import { type RecommendationTypeEnum } from 'domains/shifts/api'
import { type AllocationType, type ShopperWithShifts } from 'domains/shifts/components/types'

export interface PartnerPickRecurringShiftsModalProps {
  warehouseLocationId: string
  date: string
  onCancel: () => void
  formData: Partial<PartnerPickRecurringShiftsFormData>
  onSave: (
    savedShift: PartnerPickRecurringShiftsRecurringShift,
    shopperId: number,
    shiftId: number,
    conflictIds: string[]
  ) => void
  onDelete: (shopperId: number, shiftId: number) => void
  onAddNonRecurringShift: (
    shopperId: number,
    start: Date,
    end: Date,
    onCall: boolean
  ) => Promise<any>
  onUpdateNonRecurringShift: (
    shopperId: number,
    shiftId: number,
    recurringShiftId: number,
    start: Date,
    end: Date,
    onCall: boolean
  ) => Promise<any>
  getLocalShopperDetails: (shopperId: number) => object
  deleteNonRecurringShift: (shopperId: number, shiftId: number) => Promise<any>
  modalStyles?: Styles
  zoneOffset: number
  devicePrioritizationEnabled: boolean
  recurShiftsToBeNonRecur: number[]
  ultrafastCrossDayDeliveryEnabled: boolean
}

export interface PartnerPickRecurringShiftsFormProps {
  formData: Partial<PartnerPickRecurringShiftsFormData>
  onCancel: () => void
  onSave: (
    SavedShift: PartnerPickRecurringShiftsRecurringShift,
    shopperId: number,
    shiftId: number,
    conflictIds: string[]
  ) => void
  onDelete: (shopperId: number, shiftId: number) => void
  date: string
  warehouseLocationId: string
  onAddNonRecurringShift: (
    shopperId: number,
    start: Date,
    end: Date,
    onCall: boolean
  ) => Promise<any>
  onUpdateNonRecurringShift: (
    shopperId: number,
    shiftId: number,
    recurringShiftId: number,
    start: Date,
    end: Date,
    onCall: boolean
  ) => Promise<any>
  getLocalShopperDetails: (shopperId: number) => object
  deleteNonRecurringShift: (shopperId: number, shiftId: number) => Promise<any>
  zoneOffset: number
  onApiError: () => void
  devicePrioritizationEnabled: boolean
  recurShiftsToBeNonRecur: number[]
  ultrafastCrossDayDeliveryEnabled: boolean
}

export interface PartnerPickRecurringShiftsFormData {
  from: TimeDropdownOptionType
  to: TimeDropdownOptionType
  repeatShift?: { label: string; value: RepeatShiftEnum } // this needs to be derived
  daysOfWeek: DayOfWeekOption[]
  recurStartDate?: Moment // in edit mode recur start date may not be the same as the date from URL, in case of create it maps 1:1 with date in URL
  recurEndDate?: Moment
  shiftAllocationType?: AllocationType
  shoppers?: ShopperWithShifts[]
}

export interface RecurringShift {
  startsAt: string
  endsAt: string
  id: string
  recurringShiftId: string
  shiftType: string // runner / picker
}

export interface DayOfWeekOption {
  label: string
  value: DayOfWeekValue // Day of week represented as an Int (0-6). Sunday is 0
}
// Sunday is 0
type DayOfWeekValue = 0 | 1 | 2 | 3 | 4 | 5 | 6

export interface TimeDropdownOptionType {
  label: string
  value: number // secondsSinceMidnight
}

export interface RepeatShiftOption {
  label: string
  value: RepeatShiftEnum
}

export enum RepeatShiftEnum {
  DoesNotRepeat = 'DOES_NOT_REPEAT',
  WeeklyOnDay = 'WEEKLY_ON_DAY',
  EveryWeekday = 'EVERY_WEEKDAY',
  Daily = 'DAILY',
  Custom = 'CUSTOM',
}

export interface AvailableShopperOption {
  label: string
  value: string
}

export interface OpenShiftsModalArgs {
  formData: PartnerPickRecurringShiftsFormData
  shopperId: number
  shiftId?: number
  recurringShiftId?: number // edit mode: when this is present
  modalStyles?: Styles // used for positioning modal near corresponding shift cell
  onClose?: () => void // this will be run when the modal is closed by clicking the 'x' icon
  isEditMode?: boolean
  isRecommendationMode?: boolean
  recommendationType?: RecommendationTypeEnum
}
